import type {
  CatalogResponseDto,
  ComparisonRequestDto,
  ComparisonResponseDto,
  MeasureResponseDto,
} from './benchmark-comparison.dto';
import type { MatrixCatalog, MatrixMeasure, MatrixRequest, MatrixResult } from './types';

export const fromMeasure = (dto: MeasureResponseDto): MatrixMeasure => ({
  id: dto.id,
  name: dto.name,
  category: dto.ex_area,
  order: dto.display_order,
});

export function fromCatalog(dto: CatalogResponseDto): MatrixCatalog {
  return {
    measureId: dto.benchmark_measure.id,
    levels: [...new Map(dto.benchmark_levels.map((level) => [level.key, level])).values()],
    truncated: Object.values(dto.truncated).some(Boolean),
    pairs: dto.comparison_options.flatMap((pair) => {
      const wave = dto.waves.find((item) =>
        item.data_points.some((point) => point.id === pair.external_benchmark_data_point_id),
      );
      const point = wave?.data_points.find((item) => item.id === pair.external_benchmark_data_point_id);
      const definition = dto.internal_definitions.find((item) =>
        item.data_points.some((point) => point.id === pair.internal_aggregate_data_point_id),
      );
      if (!wave || !point || !definition) return [];
      return [
        {
          id: JSON.stringify([pair.internal_aggregate_data_point_id, pair.external_benchmark_data_point_id]),
          internalId: pair.internal_aggregate_data_point_id,
          externalId: pair.external_benchmark_data_point_id,
          internalName: definition.name,
          externalName: point.name,
          waveName: wave.name,
          period: JSON.stringify([
            pair.period_comparison.internal_period_start_at,
            pair.period_comparison.internal_period_end_at,
          ]),
          coverage: JSON.stringify([
            wave.id,
            pair.period_comparison.external_period_start_at,
            pair.period_comparison.external_period_end_at,
          ]),
          levelKeys: pair.benchmark_levels,
        },
      ];
    }),
  };
}

export function toComparison(row: MatrixRequest, dimensionValues: Record<string, string>): ComparisonRequestDto {
  return {
    benchmark_measure_id: row.measureId,
    internal_aggregate_data_point_id: row.internalId,
    external_benchmark_data_point_id: row.externalId,
    benchmark_levels: row.levelKeys,
    dimension_values: dimensionValues,
  };
}

export function fromComparison(dto: ComparisonResponseDto): MatrixResult {
  const fromCell = (
    cell: ComparisonResponseDto['internal_aggregate_definition']['data_point']['current_snapshot']['selected_aggregate'],
  ) => ({
    status: cell.status,
    reason: cell.reason,
    value: cell.value,
    sampleSize: cell.sample_size,
    completeness: cell.completeness,
  });
  return {
    measureId: dto.benchmark_measure.id,
    internal: fromCell(dto.internal_aggregate_definition.data_point.current_snapshot.selected_aggregate),
    benchmarks: dto.external_benchmark_wave.data_point.benchmark_results.map((item) => ({
      key: item.benchmark_level.key,
      cell: fromCell(item.snapshot.selected_aggregate),
      status: item.comparison.status,
      gap: item.comparison.gap,
    })),
    relationship: dto.period_comparison.relationship,
  };
}
